<template>
    <div class="table-container w-full">
        <div class="list-top-container">
            <div class="list-search-container w-full mb-2 md:w-auto md:mb-0">
                <a-input-search
                    data-cy="migration-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>

            <div class="list-buttons w-full mb-2 md:w-auto md:mb-0">
                <a-button-group>
                    <a-button @click="onAdd" data-cy="btn-create-item" type="primary">
                        {{ $t('admin.migration.table.buttons.add') }}
                    </a-button>
                </a-button-group>
            </div>
        </div>

        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="migration-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'actions'">
                            <a-button-group>
                                <a-tooltip placement="top" :title="$t('admin.migration.table.buttons.view')">
                                    <a-button
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        @click="onTableAction(record.id, 'view')"
                                    >
                                        <FolderOpenOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip
                                    placement="top"
                                    :title="$t('admin.migration.table.buttons.stop')"
                                    v-if="!['failed', 'stopped'].includes(record.status)"
                                >
                                    <a-button
                                        class="text-red-400 hover:text-red-400 focus:text-red-400"
                                        @click="onTableAction(record.id, 'stop')"
                                    >
                                        <StopOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip
                                    placement="top"
                                    :title="$t('admin.migration.table.buttons.restart')"
                                    v-if="['failed', 'stopped'].includes(record.status)"
                                >
                                    <a-button
                                        class="text-green-400 hover:text-green-400 focus:text-green-400"
                                        @click="onTableAction(record.id, 'restart')"
                                    >
                                        <RedoOutlined />
                                    </a-button>
                                </a-tooltip>
                            </a-button-group>
                        </template>

                        <template v-else-if="column.key === 'timeago'"> {{ text }} {{ timeAgo(text) }} </template>
                        <template v-else-if="column.key === 'status'">
                            <a-tag v-if="text == 'pending'" color="orange">
                                {{ $t('admin.migration.table.labels.pending') }}
                            </a-tag>
                            <a-tag v-if="text == 'running'" color="blue">
                                {{ $t('admin.migration.table.labels.running') }}
                            </a-tag>
                            <a-tag v-if="text == 'completed'" color="green">
                                {{ $t('admin.migration.table.labels.completed') }}
                            </a-tag>
                            <a-tag v-if="text == 'failed'" color="red">
                                {{ $t('admin.migration.table.labels.failed') }}
                            </a-tag>
                            <a-tag v-if="text == 'stopped'" color="yellow">
                                {{ $t('admin.migration.table.labels.stopped') }}
                            </a-tag>
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>

                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { FolderOpenOutlined, RedoOutlined, StopOutlined } from '@ant-design/icons-vue';

export default {
    components: { FolderOpenOutlined, RedoOutlined, StopOutlined },
    data() {
        return {
            data: [],
            search_value: '',
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
            migration_item: {},
        };
    },
    mounted() {
        this.fetch();
    },
    computed: {
        getColumns() {
            return [
                {
                    key: 'actions',
                    width: 35,
                },
                {
                    title: this.$t('globals.tables.migration.headers.migration_name'),
                    key: 'migration_name',
                    dataIndex: 'migration_name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration.headers.status'),
                    key: 'status',
                    dataIndex: 'status',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration.headers.migration_started_at'),
                    key: 'timeago',
                    dataIndex: 'migration_started_at',
                    defaultSortOrder: 'descend',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.migration.headers.migration_ended_at'),
                    key: 'timeago',
                    dataIndex: 'migration_ended_at',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
    },
    methods: {
        timeAgo(datetime) {
            let mjs = this.$dayjs(datetime);

            return mjs.isValid() ? '(' + mjs.fromNow() + ')' : '-';
        },
        fetch(params = {}) {
            this.loading = true;
            this.params = params;

            this.$http
                .get('/api/migrations', { params: params })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;
                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVMGF01' }),
                        });
                    }
                });
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
        onTableAction(id, key) {
            switch (key) {
                case 'view':
                case 'stop':
                case 'restart':
                    this.$emit('onTableAction', {
                        id: id,
                        key: key,
                    });
                    break;
            }
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                this.search_value = input;

                this.fetch({
                    search_value: this.search_value,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        onAdd() {
            this.$emit('onTableAction', {
                id: null,
                key: 'view',
            });
        },
    },
};
</script>
